import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import { formatCurrency, formatDate, formatThousands } from "@/utils";

const StockOutReportPrint = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const params = {
        start_date: searchParams.get("start_date") || "",
        end_date: searchParams.get("end_date") || "",
        warehouse_id: searchParams.get("warehouse_id") || undefined,
        material_id: searchParams.get("material_id") || undefined,
        sub_category_id: searchParams.get("sub_category_id") || undefined,
    };

    const { data, isLoading, error } = useFetchData(
        "report/stock-out",
        params,
    );

    const transactions = data?.transactions || [];
    const recapByCategory = data?.recap_by_category || [];
    const recapByWarehouse = data?.recap_by_warehouse || [];
    const period = data?.period || {};

    const grandTotalValue = transactions.reduce(
        (sum, item) => sum + Number(item.total_value || 0),
        0,
    );
    const recapCategoryTotal = recapByCategory.reduce(
        (sum, item) => sum + Number(item.total_value || 0),
        0,
    );
    const recapWarehouseTotal = recapByWarehouse.reduce(
        (sum, item) => sum + Number(item.total_value || 0),
        0,
    );

    const periodDisplay =
        period.start === period.end
            ? formatDate(period.start)
            : `${formatDate(period.start)} - ${formatDate(period.end)}`;

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/stock-out-report")}
                >
                    Kembali
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[210mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/stock-out-report")}
                >
                    Kembali
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title =
                            period.start &&
                            period.end &&
                            period.start !== period.end
                                ? `Laporan_Stok_Keluar_${period.start}_sd_${period.end}`
                                : `Laporan_Stok_Keluar_${period.start || ""}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="LAPORAN STOK KELUAR" />

                <div className="mb-2 text-xs">
                    <span className="font-medium">Periode: </span>
                    {periodDisplay}
                </div>

                {/* Transactions Table */}
                {transactions.length > 0 && (
                    <table className="mb-6 w-full border-collapse border border-black text-[11px]">
                        <thead>
                            <tr className="bg-default-100 whitespace-nowrap">
                                <th className="border border-black px-1 py-px text-center">
                                    No.
                                </th>
                                <th className="border border-black px-1 py-px text-center">
                                    Tanggal
                                </th>
                                <th className="border border-black px-1 py-px text-left">
                                    Produk
                                </th>
                                <th className="border border-black px-1 py-px text-left">
                                    Gudang
                                </th>
                                <th className="border border-black px-1 py-px text-right">
                                    Jumlah
                                </th>
                                <th className="border border-black px-1 py-px text-right">
                                    @Cost
                                </th>
                                <th className="border border-black px-1 py-px text-right">
                                    Total Nilai
                                </th>
                                <th className="border border-black px-1 py-px text-left">
                                    Keterangan
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((item, index) => (
                                <tr key={item.id}>
                                    <td className="border border-black px-1 py-px text-center">
                                        {index + 1}.
                                    </td>
                                    <td className="border border-black px-1 py-px text-center whitespace-nowrap">
                                        {formatDate(item.date)}
                                    </td>
                                    <td className="border border-black px-1 py-px">
                                        {item.material_name}
                                        <span className="ml-1 text-[9px]">
                                            ({item.sub_category_name})
                                        </span>
                                    </td>
                                    <td className="border border-black px-1 py-px whitespace-nowrap">
                                        {item.warehouse_name}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right whitespace-nowrap">
                                        {formatThousands(item.qty_out)}{" "}
                                        {item.unit_name}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatCurrency(item.cost)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatCurrency(item.total_value)}
                                    </td>
                                    <td className="border border-black px-1 py-px">
                                        {item.description || "-"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-default-100 font-bold">
                                <td
                                    colSpan={6}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    TOTAL
                                </td>
                                <td className="border border-black px-1 py-px text-right">
                                    {formatCurrency(grandTotalValue)}
                                </td>
                                <td className="border border-black px-1 py-px"></td>
                            </tr>
                        </tfoot>
                    </table>
                )}

                {/* Recap per Kategori */}
                {recapByCategory.length > 0 && (
                    <>
                        <div className="mb-2 text-sm font-bold">
                            Rekap per Kategori Produk
                        </div>
                        <table className="mb-6 w-full border-collapse border border-black text-[11px]">
                            <thead>
                                <tr className="bg-default-100 whitespace-nowrap">
                                    <th className="border border-black px-1 py-px text-center">
                                        No.
                                    </th>
                                    <th className="border border-black px-1 py-px text-left">
                                        Kategori Produk
                                    </th>
                                    <th className="border border-black px-1 py-px text-right">
                                        Total Qty
                                    </th>
                                    <th className="border border-black px-1 py-px text-right">
                                        Total Nilai
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {recapByCategory.map((item, index) => (
                                    <tr key={index}>
                                        <td className="border border-black px-1 py-px text-center">
                                            {index + 1}.
                                        </td>
                                        <td className="border border-black px-1 py-px">
                                            {item.sub_category_name}
                                        </td>
                                        <td className="border border-black px-1 py-px text-right">
                                            {formatThousands(item.total_qty)}
                                        </td>
                                        <td className="border border-black px-1 py-px text-right">
                                            {formatCurrency(item.total_value)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-default-100 font-bold">
                                    <td
                                        colSpan={3}
                                        className="border border-black px-1 py-px text-center"
                                    >
                                        TOTAL
                                    </td>
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatCurrency(recapCategoryTotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {/* Recap per Gudang */}
                {recapByWarehouse.length > 0 && (
                    <>
                        <div className="mb-2 text-sm font-bold">
                            Rekap per Gudang
                        </div>
                        <table className="mb-6 w-full border-collapse border border-black text-[11px]">
                            <thead>
                                <tr className="bg-default-100 whitespace-nowrap">
                                    <th className="border border-black px-1 py-px text-center">
                                        No.
                                    </th>
                                    <th className="border border-black px-1 py-px text-left">
                                        Gudang
                                    </th>
                                    <th className="border border-black px-1 py-px text-right">
                                        Total Qty
                                    </th>
                                    <th className="border border-black px-1 py-px text-right">
                                        Total Nilai
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {recapByWarehouse.map((item, index) => (
                                    <tr key={index}>
                                        <td className="border border-black px-1 py-px text-center">
                                            {index + 1}.
                                        </td>
                                        <td className="border border-black px-1 py-px">
                                            {item.warehouse_name}
                                        </td>
                                        <td className="border border-black px-1 py-px text-right">
                                            {formatThousands(item.total_qty)}
                                        </td>
                                        <td className="border border-black px-1 py-px text-right">
                                            {formatCurrency(item.total_value)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-default-100 font-bold">
                                    <td
                                        colSpan={3}
                                        className="border border-black px-1 py-px text-center"
                                    >
                                        TOTAL
                                    </td>
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatCurrency(recapWarehouseTotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {/* Footer */}
                <div className="text-default-500 mt-8 text-xs">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default StockOutReportPrint;
